<script setup lang="ts">
import { ref, reactive, computed, defineProps, defineEmits, nextTick } from 'vue'
import type { FormInstance, FormRules, UploadProps, UploadUserFile } from 'element-plus'
import axios from 'axios'
import config from '@/utils/config'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { UPLOAD_IMAGE_EXTENSIONS, UPLOAD_IMAGE_ACCEPT, UPLOAD_IMAGE_FORMAT_LABEL } from '@/utils/upload'

const userStore = useUserStore()


const BaseUrl = config.BASE_URL

interface FeedbackForm {
  submission_id: number
  submission_time: string
  submitter: string
  page_name: string
  module_name: string
  issue_details: string
  issue_image: string | null
  image_filename: string | null
  image_url: string | null
}

interface PageOption {
  value: string
  label: string
}

interface ModuleOption {
  [key: string]: PageOption[]
}

const pageOptions: PageOption[] = [
  { value: '舆情看板', label: '舆情看板' },
  { value: '舆情详情', label: '舆情详情' },
  { value: '舆情内容', label: '舆情内容' },
  { value: '归档看板', label: '归档看板' },
  { value: '归档详情', label: '归档详情' },
  { value: '流程监听', label: '流程监听' },
  { value: '内部详情', label: '内部详情' },
  { value: 'Zmn工单详情', label: 'Zmn工单详情' },
  { value: '言而有信工单详情', label: '言而有信工单详情' },
  { value: '投诉详情', label: '投诉详情' },
  { value: '个人信息', label: '个人信息' },
  { value: '权限管理', label: '权限管理' },
  { value: '主管部门', label: '主管部门' },
  { value: '合作服务商', label: '合作服务商' },
  { value: '媒体资源库', label: '媒体资源库' },
  { value: '媒体报道', label: '媒体报道' },
  { value: '媒体资源看板', label: '媒体资源看板' },
  { value: '媒体详情', label: '媒体详情' },
  { value: '任务追踪', label: '任务追踪' },
  { value: '追责看板', label: '追责看板' },
  { value: '追责列表', label: '追责列表' },
  { value: '整改任务', label: '整改任务' }
]

const moduleOptions: ModuleOption = {
  舆情看板: [
    { value: '舆情总览实时数据', label: '舆情总览实时数据' },
    { value: '舆情热搜', label: '舆情热搜' },
    { value: '舆情等级分布与走势', label: '舆情等级分布与走势' },
    { value: '黑公关监测', label: '黑公关监测' },
    { value: '活跃作者', label: '活跃作者' },
    { value: '舆情关键词', label: '舆情关键词' },
    { value: '舆情性质分类', label: '舆情性质分类' },
    { value: '全国舆情热点图', label: '全国舆情热点图' },
    { value: '舆情分布概览', label: '舆情分布概览' }
  ],
  舆情详情: [
    { value: '数据列表模块', label: '数据列表模块' },
    { value: '导入模块', label: '导入模块' },
    { value: '右侧搜索模块', label: '右侧搜索模块' },
    { value: '顶部搜索模块', label: '顶部搜索模块' }
  ],
  舆情内容: [
    { value: '舆情信息', label: '舆情信息' },
    { value: '本贴关键词', label: '本贴关键词' },
    { value: '舆情内容详情', label: '舆情内容详情' },
    { value: 'AI内容分析', label: 'AI内容分析' },
    { value: '发布者信息', label: '发布者信息' }
  ],
  归档看板: [
    { value: '顶部统计模块', label: '顶部统计模块' },
    { value: '搜索模块', label: '搜索模块' },
    { value: '数据列表模块', label: '数据列表模块' },
    { value: '分页模块', label: '分页模块' }
  ],
  归档详情: [
    { value: '基本信息', label: '基本信息' },
    { value: '投诉详情', label: '投诉详情' },
    { value: '处理结果', label: '处理结果' },
    { value: '流程详情', label: '流程详情' },
  ],
  流程监听: [
    { value: '数据列表模块', label: '数据列表模块' },
    { value: '流程详情模块', label: '流程详情模块' }
  ],
  内部详情: [
    { value: '数据列表模块', label: '数据列表模块' },
    { value: '分页模块', label: '分页模块' },
    { value: '搜索模块', label: '搜索模块' },
    { value: '舆情分发', label: '舆情分发' }
  ],
  'Zmn工单详情': [
    { value: '工单信息', label: '工单信息' },
    { value: '用户信息', label: '用户信息' },
    { value: '工程师信息', label: '工程师信息' },
    { value: '关联信息', label: '关联信息' },
    { value: '下单信息', label: '下单信息' },
    { value: '专家维修方案', label: '专家维修方案' },
    { value: '子公司信息', label: '子公司信息' },
    { value: '推单信息', label: '推单信息' },
    { value: '定制门窗供应链信息', label: '定制门窗供应链信息' },
    { value: '定制门窗报价信息', label: '定制门窗报价信息' },
    { value: '收入相关', label: '收入相关' },
    { value: '配件信息', label: '配件信息' },
    { value: '照片信息', label: '照片信息' },
    { value: '评价信息', label: '评价信息' },
    { value: '售后信息及其他', label: '售后信息及其他' },
    { value: '操作日志', label: '操作日志' }
  ],
  言而有信工单详情: [
    { value: '订单标记', label: '订单标记' },
    { value: '订单信息', label: '订单信息' },
    { value: '关联线索', label: '关联线索' },
    { value: '用户信息', label: '用户信息' },
    { value: '接单信息', label: '接单信息' },
    { value: '报价信息', label: '报价信息' },
    { value: '服务信息', label: '服务信息' },
    { value: '收费明细', label: '收费明细' },
    { value: '结算明细', label: '结算明细' },
    { value: '积分明细', label: '积分明细' },
    { value: '服务项目', label: '服务项目' },
    { value: '附加项目', label: '附加项目' },
    { value: '关联虚假', label: '关联虚假' },
    { value: '关联转化单', label: '关联转化单' },
    { value: '关联跟单', label: '关联跟单' },
    { value: '售后信息', label: '售后信息' },
    { value: '历史记录', label: '历史记录' }
  ],
  投诉详情: [
    { value: '投诉信息模块', label: '投诉信息模块' },
    { value: '处理进度模块', label: '处理进度模块' }
  ],
  个人信息: [
    { value: '个人信息', label: '个人信息' },
    { value: '我的收藏', label: '我的收藏' }
  ],
  权限管理: [
    { value: '权限管理列表', label: '权限管理列表' },
    { value: '搜索模块', label: '搜索模块' }
  ],
  主管部门: [
    { value: '活动排期', label: '活动排期' },
    { value: '媒体报道列表', label: '媒体报道列表' }
  ],
  合作服务商: [
    { value: '合作列表', label: '合作列表' },
    { value: '合作排期', label: '合作排期' }
  ],
  媒体资源库: [
    { value: '搜索模块', label: '搜索模块' },
    { value: '合作媒体列表', label: '合作媒体列表' }
  ],
  媒体报道: [
    { value: '搜索模块', label: '搜索模块' },
    { value: '具体内容模块', label: '具体内容模块' }
  ],
  媒体资源看板: [
    { value: '顶部统计模块', label: '顶部统计模块' },
    { value: '报道统计概览', label: '报道统计概览' },
    { value: '媒体统计概览', label: '媒体统计概览' },
    { value: '合作媒体列表', label: '合作媒体列表' },
    { value: '顶部统计模块', label: '顶部统计模块' },
    { value: '报道统计概览', label: '报道统计概览' },
  ],
  媒体详情: [
    { value: '媒体基本信息', label: '媒体基本信息' },
    { value: '账号信息', label: '账号信息' },
    { value: '合作信息', label: '合作信息' },
    { value: '内容溯源', label: '内容溯源' },
    { value: '合作对接', label: '合作对接' },
    { value: '资源消耗', label: '资源消耗' }
  ],
  任务追踪: [
    { value: '任务信息', label: '任务信息' },
    { value: '执行进度模块', label: '执行进度模块' }
  ],
  追责看板: [
    { value: '顶部统计模块', label: '顶部统计模块' },
    { value: '投诉内容', label: '投诉内容' },
    { value: '被追责部门', label: '被追责部门' },
    { value: '奖惩记录', label: '奖惩记录' },
  ],
  追责列表: [
    { value: '搜索模块', label: '搜索模块' },
    { value: '追责列表', label: '追责列表' }
  ],
  整改任务: [
    { value: '顶部统计模块', label: '顶部统计模块' },
    { value: '搜索模块', label: '搜索模块' },
    { value: '整改任务列表', label: '整改任务列表' }
  ]
}

const visible = ref(false)
const form = reactive<FeedbackForm>({
  submission_id: 0,
  submission_time: '',
  submitter: '',
  page_name: '',
  module_name: '',
  issue_details: '',
  issue_image: null,
  image_filename: null,
  image_url: null
})

// 保存原始文件对象，用于上传
const currentFile = ref<File | null>(null)

const fileList = ref<UploadUserFile[]>([])

const currentModuleOptions = computed(() => {
  if (!form.page_name) return []
  return moduleOptions[form.page_name] || []
})

const handlePageChange = () => {
  form.module_name = ''
}


const beforeUpload: UploadProps['beforeUpload'] = (rawFile) => {
  const extension = rawFile.name.split('.').pop()?.toLowerCase() || ''
  if (!UPLOAD_IMAGE_EXTENSIONS.includes(extension)) {
    console.error(`仅支持图片格式：${UPLOAD_IMAGE_FORMAT_LABEL}`)
    return false
  }
  const isLt5M = rawFile.size / 1024 / 1024 < 5
  if (!isLt5M) {
    console.error('图片大小不能超过 5MB!')
    return false
  }
  return true
}


const handleFileChange: UploadProps['onChange'] = (uploadFile) => {
  if (uploadFile && uploadFile.raw) {
    // 保存文件对象和文件名，等待提交时上传
    currentFile.value = uploadFile.raw
    form.image_filename = uploadFile.raw.name
    fileList.value = [uploadFile]
    ElMessage.success('图片已选择，提交时将上传')
  }
}


const handleRemove: UploadProps['onRemove'] = () => {
  form.issue_image = null
  form.image_filename = null
  form.image_url = null
  currentFile.value = null
  fileList.value = []
}


const handleExceed: UploadProps['onExceed'] = () => {
  ElMessage.warning('只能上传 1 张图片')
}


const open = () => {
  resetForm(); // 先重置表单
  visible.value = true;
  // 如果父组件传递了当前页面名称，自动填充到表单
  if (props.currentPage) {
    form.page_name = props.currentPage;
  }
  console.log('currentPage:', props.currentPage)
  // 清除表单验证状态，避免打开时立即显示错误
  nextTick(() => {
    formRef.value?.clearValidate()
  })
}


const handleClose = () => {
  visible.value = false
  resetForm()
  emit('close')
}

const resetForm = () => {
  Object.assign(form, {
    submission_id: 0,
    submission_time: '',
    submitter: '',
    page_name: '',
    module_name: '',
    issue_details: '',
    issue_image: null,
    image_filename: null,
    image_url: null
  } as FeedbackForm)
  currentFile.value = null
  fileList.value = []
}

defineExpose({ open })


const formRef = ref<FormInstance>()
const rules: FormRules = {
  page_name: [{ required: true, message: '请选择页面名称', trigger: 'blur' }],
  module_name: [{ required: true, message: '请选择模块名称', trigger: 'blur' }],
  issue_details: [{ required: true, message: '请输入问题详情', trigger: 'blur' }]
}



// 上传图片到服务器
// 上传图片到服务器（JSON方式：fileBytes + fileName）
const uploadImageToServer = async (file: File): Promise<string> => {
  try {
    // 1. 读取文件为字节数组
    const buffer = await file.arrayBuffer()
    const fileBytes = Array.from(new Uint8Array(buffer))

    // 2. 组装后端需要的 payload
    const payload = {
      fileName: file.name,
      fileBytes
    }

    // 3. 调用上传接口（JSON 方式，不是 multipart）
    const res = await axios.post(
      `${BaseUrl}/tgsys/uploadFile`,
      payload,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    // 🔍 打印返回结构，方便排查
    console.log('上传接口返回：', res.data)

    /**
     * 后端真实返回结构：
     * {
     *   code: 200,
     *   msg: 'success',
     *   data: {
     *     data: {
     *       fileName: 'xxx.png',
     *       url: 'https://xxx'
     *     },
     *     status: 200,
     *     success: true
     *   }
     * }
     */

      // 4. 精准 & 兜底式解析 URL
    const url =
        res.data?.data?.data?.url || // ✅ 当前真实结构
        res.data?.data?.url ||       // 兜底 1
        res.data?.url ||             // 兜底 2
        (typeof res.data === 'string' ? res.data : '') // 兜底 3

    if (!url) {
      throw new Error('上传成功但未返回图片地址')
    }

    return url
  } catch (error) {
    console.error('图片上传失败:', error)
    throw error
  }
}

const submitting = ref(false)

const onSubmit = async () => {
  if (!formRef.value || submitting.value) return

  submitting.value = true

  try {
    console.log('开始表单验证...')
    await formRef.value.validate()
    console.log('表单验证通过')

    // 上传图片
    if (currentFile.value) {
      ElMessage.info('正在上传图片...')
      const imageUrl = await uploadImageToServer(currentFile.value)
      form.image_url = imageUrl
      ElMessage.success('图片上传成功')
    }

    // 组装数据
    form.submission_time = new Date().toISOString()
    form.submitter = userStore.name || 'Anonymous'
    console.log("提交人名字：" , form.submitter )
    const unique_id = `${form.page_name}_${form.module_name}_${form.submission_time}`

    const payload = {
      ...form,
      unique_id,
      issue_image: null
    }

    console.log('准备提交表单，payload:', payload)
    ElMessage.info('正在提交反馈...')

    const response = await axios.post(`${BaseUrl}/api/issue`, payload, {
      withCredentials: true
    })

    console.log('提交接口返回:', response.data)

    // ✅ 明确判断是否成功
    if (response.data?.code === 200 || response.data?.success) {
      ElMessage.success('提交成功')
      handleClose()
    } else {
      throw new Error(response.data?.msg || '提交失败')
    }
  } catch (error: any) {
    console.error('提交失败:', error)
    console.error(error.message || '提交失败，请稍后再试')
  } finally {
    submitting.value = false
  }
}



//问题反馈图标
// 定义关闭事件，用于通知父组件
const emit = defineEmits(['close'])


//问题反馈自动定位
// 定义接收父组件传递的当前页面名称
const props = defineProps<{
  currentPage?: string; // 可选，父组件传递的当前页面名称
}>();

const onCancel = () => handleClose()
</script>

<template>
  <el-dialog
    title="问题反馈"
    v-model="visible"
    width="720px"
    @close="handleClose"
    class="feedback-dialog"
    :close-on-click-modal="false"
  >
    <el-form
      :model="form"
      label-width="100px"
      class="feedback-form"
      label-position="left"
      :rules="rules"
      ref="formRef"
    >
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="页面" prop="page_name">
            <el-select v-model="form.page_name" placeholder="请选择页面" @change="handlePageChange">
              <el-option
                v-for="page in pageOptions"
                :key="page.value"
                :value="page.value"
                :label="page.label"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="模块" prop="module_name">
            <el-select v-model="form.module_name" placeholder="请选择模块">
              <el-option
                v-for="module in currentModuleOptions"
                :key="module.value"
                :value="module.value"
                :label="module.label"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="24">
          <el-form-item label="问题详情" prop="issue_details">
            <el-input
              v-model="form.issue_details"
              type="textarea"
              rows="6"
              placeholder="请输入问题详细描述..."
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="24">
          <el-form-item>
            <template #label>
              图片上传<br>(限制一张，支持 {{ UPLOAD_IMAGE_FORMAT_LABEL }})
            </template>
            <el-upload
              :file-list="fileList"
              :before-upload="beforeUpload"
              :on-change="handleFileChange"
              :on-remove="handleRemove"
              :on-exceed="handleExceed"
              :auto-upload="false"
              :accept="UPLOAD_IMAGE_ACCEPT"
              :limit="1"
              class="custom-upload"
            >
              <div v-if="!form.issue_image" class="upload-placeholder">
                <span class="upload-text">点击上传图片</span>
              </div>

              <div v-else class="upload-preview">
                <el-image
                  :src="form.issue_image"
                  fit="contain"
                  class="preview-image"
                  :preview-src-list="[form.issue_image]"
                />
                <div class="preview-mask">
                  <el-button size="small" type="danger" @click.stop="handleRemove()">移除</el-button>
                </div>
              </div>
            </el-upload>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row class="action-row">
        <div class="button-group">
          <el-button type="primary" @click="onSubmit">提交</el-button>
          <el-button @click="onCancel">取消</el-button>
        </div>
      </el-row>
    </el-form>
  </el-dialog>
</template>

<style scoped>
.feedback-form {
  padding: 32px;
}

.upload-placeholder {
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  transition: all 0.3s ease;
  width: 100%;
  cursor: pointer;
}
.upload-placeholder:hover {
  border-color: #409eff;
  background: #f0f7ff;
}
.upload-text {
  color: #909399;
  font-size: 14px;
}

.upload-preview {
  position: relative;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
  background: #fafafa;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
}
.preview-image {
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  border-radius: 6px;
}
.preview-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  transition: opacity 0.3s;
}
.upload-preview:hover .preview-mask {
  opacity: 1;
}

.action-row {
  margin-top: 8px;
}
.button-group {
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
}
</style>
